import React, { useEffect, useState } from "react";
import {
  ArrowForward,
  CheckCircle,
  Hub,
  Inventory2,
  AccountTree,
  Security,
  Analytics,
  Groups,
  Storefront,
  Warehouse,
  PointOfSale,
  Smartphone,
  QrCodeScanner,
  CloudSync,
  Api,
  NotificationsActive,
  Lock,
  VerifiedUser,
  TrendingUp,
  Speed,
  Business,
  ManageAccounts,
  Assessment,
  SwapHoriz,
  ReceiptLong,
  SupportAgent,
  SettingsSuggest,
  Language,
  RocketLaunch,
  ExpandMore,
} from "@mui/icons-material";
import "./Enterprise.css";

const ENTERPRISE_CAPABILITIES = [
  {
    icon: <Hub />,
    title: "One Command Center",
    description:
      "Bring your entire inventory operation together. Monitor branches, warehouses, products, sales, purchases, transfers, and operational activity from one centralized environment.",
  },
  {
    icon: <AccountTree />,
    title: "Multi-Branch Architecture",
    description:
      "Create a connected structure for headquarters, branches, warehouses, stores, departments, and operational units while maintaining centralized visibility.",
  },
  {
    icon: <ManageAccounts />,
    title: "Advanced Access Control",
    description:
      "Give every employee exactly the level of access required for their responsibility. Separate administrative, managerial, financial, warehouse, POS, and operational permissions.",
  },
  {
    icon: <Analytics />,
    title: "Enterprise Intelligence",
    description:
      "Turn operational data into decisions with powerful dashboards, inventory analytics, sales performance, purchasing insights, and business reports.",
  },
  {
    icon: <CloudSync />,
    title: "Connected Operations",
    description:
      "Keep your business ecosystem synchronized as inventory, sales, purchases, transfers, payments, and operational events move through the platform.",
  },
  {
    icon: <Api />,
    title: "Integration Ready",
    description:
      "Connect Universe Inventory with your wider technology ecosystem through APIs, integrations, and purpose-built business workflows.",
  },
];

const ENTERPRISE_SOLUTIONS = [
  {
    icon: <Warehouse />,
    title: "Warehouse Networks",
    description:
      "Manage stock across multiple warehouses and operational locations while maintaining a unified view of inventory movement.",
    points: [
      "Central warehouse visibility",
      "Stock transfers",
      "Receiving & dispatch",
      "Inventory adjustments",
    ],
  },
  {
    icon: <Storefront />,
    title: "Retail Networks",
    description:
      "Connect multiple stores and selling locations while giving headquarters the visibility required to understand the entire retail network.",
    points: [
      "Multi-store operations",
      "Centralized product control",
      "Store-level performance",
      "POS synchronization",
    ],
  },
  {
    icon: <Business />,
    title: "Corporate Operations",
    description:
      "Give management a unified operational layer for controlling inventory, staff activity, transactions, and business performance.",
    points: [
      "Executive visibility",
      "Departmental access",
      "Operational reporting",
      "Central administration",
    ],
  },
  {
    icon: <AccountTree />,
    title: "Distribution Networks",
    description:
      "Coordinate inventory movement between suppliers, warehouses, branches, stores, and customers with greater traceability.",
    points: [
      "Distribution visibility",
      "Transfer management",
      "Supplier operations",
      "Stock accountability",
    ],
  },
];

const ENTERPRISE_APPS = [
  {
    icon: <PointOfSale />,
    number: "01",
    title: "Universe Point of Sale",
    description:
      "Connect frontline sales with your inventory operation. Every sale becomes part of the larger business picture.",
  },
  {
    icon: <QrCodeScanner />,
    number: "02",
    title: "Cam-Scanner",
    description:
      "Turn your computer camera into a practical barcode scanning tool for fast product identification and inventory workflows.",
  },
  {
    icon: <Smartphone />,
    number: "03",
    title: "Uni-Cloud",
    description:
      "Stay connected to business activity with real-time notifications and operational updates from your inventory ecosystem.",
  },
];

const SECURITY_ITEMS = [
  {
    icon: <Lock />,
    title: "Controlled Access",
    description:
      "Restrict sensitive operations according to users, roles, responsibilities, and organizational structure.",
  },
  {
    icon: <VerifiedUser />,
    title: "Accountability",
    description:
      "Keep operational activity traceable so management can understand what happened and where it happened.",
  },
  {
    icon: <Security />,
    title: "Data Protection",
    description:
      "Build your organization's inventory operations around controlled access and responsible data management.",
  },
  {
    icon: <Groups />,
    title: "Team Governance",
    description:
      "Create a structured environment where every team member operates within the boundaries of their role.",
  },
];

const FAQS = [
  {
    question: "Who is Universe Enterprise designed for?",
    answer:
      "Universe Enterprise is designed for organizations that have more complex inventory operations, multiple locations, larger teams, specialized departments, or the need for centralized management and deeper operational visibility.",
  },
  {
    question: "Can we manage multiple branches?",
    answer:
      "Yes. Enterprise operations can be structured around multiple branches, warehouses, stores, departments, and other operational locations while maintaining centralized oversight.",
  },
  {
    question: "Can different employees have different permissions?",
    answer:
      "Yes. Enterprise environments require different levels of responsibility. Universe Inventory can be structured around roles and permissions so employees receive access appropriate to their responsibilities.",
  },
  {
    question: "Can Universe Inventory connect with our existing systems?",
    answer:
      "Universe Inventory is designed with connected business operations in mind. Enterprise organizations can discuss their integration and API requirements with the Universe team.",
  },
  {
    question: "Can our organization use the Universe mobile ecosystem?",
    answer:
      "Yes. Universe Inventory works alongside its supporting applications, including Universe Point of Sale, Cam-Scanner, and Uni-Cloud, creating a connected operational ecosystem.",
  },
];

const Enterprise = () => {
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="enterprisePage">

      {/* ================================================================
          HERO
      ================================================================ */}

      <section className="enterpriseHero">
        <div className="enterpriseHeroGrid" />

        <div className="enterpriseHeroOrb enterpriseHeroOrbOne" />
        <div className="enterpriseHeroOrb enterpriseHeroOrbTwo" />

        <div className="enterpriseHeroContent">

          <div className="enterpriseEyebrow fx-ac space">
            <span className="enterpriseEyebrowLine" />
            <span>UNIVERSE ENTERPRISE</span>
            <span className="enterpriseEyebrowDot" />
            <span>BUILT FOR SCALE</span>
          </div>

          <h1>
            Your entire inventory operation.
            <span> Under one command center.</span>
          </h1>

          <p className="enterpriseHeroDescription">
            Universe Enterprise brings inventory, branches, warehouses,
            sales, purchasing, teams, transactions, analytics, and connected
            applications into one intelligent business environment.
          </p>

          <div className="enterpriseHeroActions">
            <button className="enterprisePrimaryButton">
              Talk to Enterprise
              <ArrowForward />
            </button>

            <button className="enterpriseSecondaryButton">
              Explore the platform
              <ExpandMore />
            </button>
          </div>

          <div className="enterpriseHeroTrust">
            <div className="enterpriseTrustItem">
              <CheckCircle />
              <span>Centralized operations</span>
            </div>

            <div className="enterpriseTrustItem">
              <CheckCircle />
              <span>Multi-location ready</span>
            </div>

            <div className="enterpriseTrustItem">
              <CheckCircle />
              <span>Role-based control</span>
            </div>
          </div>

        </div>

        {/* HERO COMMAND CENTER */}

        <div className="enterpriseDashboardPreview">

          <div className="enterpriseDashboardTop">
            <div className="enterpriseDashboardBrand">
              <div className="enterpriseDashboardLogo">
                <Inventory2 />
              </div>

              <div>
                <span>Universe Enterprise</span>
                <small>Operations Command Center</small>
              </div>
            </div>

            <div className="enterpriseLiveStatus">
              <span />
              LIVE OPERATIONS
            </div>
          </div>

          <div className="enterpriseDashboardBody">

            <div className="enterpriseMetricCard">
              <div className="enterpriseMetricIcon">
                <Inventory2 />
              </div>

              <span>Total Inventory</span>
              <strong>48,291</strong>
              <small>
                <TrendingUp /> 12.8% this month
              </small>
            </div>

            <div className="enterpriseMetricCard">
              <div className="enterpriseMetricIcon">
                <Storefront />
              </div>

              <span>Active Locations</span>
              <strong>24</strong>
              <small>
                <CheckCircle /> All connected
              </small>
            </div>

            <div className="enterpriseMetricCard">
              <div className="enterpriseMetricIcon">
                <PointOfSale />
              </div>

              <span>Today's Sales</span>
              <strong>₦8.42M</strong>
              <small>
                <TrendingUp /> 18.4% growth
              </small>
            </div>

            <div className="enterpriseMetricCard">
              <div className="enterpriseMetricIcon">
                <Groups />
              </div>

              <span>Active Users</span>
              <strong>137</strong>
              <small>
                <CheckCircle /> Controlled access
              </small>
            </div>

          </div>

          <div className="enterpriseDashboardActivity">

            <div className="enterpriseActivityHeader">
              <div>
                <span>Operational Activity</span>
                <small>Real-time business activity</small>
              </div>

              <Analytics />
            </div>

            <div className="enterpriseActivityGraph">
              <span style={{ height: "34%" }} />
              <span style={{ height: "52%" }} />
              <span style={{ height: "41%" }} />
              <span style={{ height: "68%" }} />
              <span style={{ height: "56%" }} />
              <span style={{ height: "83%" }} />
              <span style={{ height: "72%" }} />
              <span style={{ height: "94%" }} />
              <span style={{ height: "79%" }} />
              <span style={{ height: "100%" }} />
            </div>

          </div>

        </div>
      </section>

      {/* ================================================================
          SCALE STATEMENT
      ================================================================ */}

      <section className="enterpriseScaleSection">

        <div className="enterpriseSectionHeading centered">
          <span className="enterpriseSectionKicker">
            BUILT BEYOND THE SINGLE STORE
          </span>

          <h2>
            When your business grows,
            <span> your system should grow with it.</span>
          </h2>

          <p>
            Enterprise inventory management is not simply about having more
            products. It is about coordinating more people, more locations,
            more transactions, and more decisions without losing control.
          </p>
        </div>

        <div className="enterpriseScaleFlow">

          <div className="enterpriseScaleNode">
            <div>
              <Storefront />
            </div>
            <span>Branches</span>
          </div>

          <ArrowForward />

          <div className="enterpriseScaleNode">
            <div>
              <Warehouse />
            </div>
            <span>Warehouses</span>
          </div>

          <ArrowForward />

          <div className="enterpriseScaleNode active">
            <div>
              <Hub />
            </div>
            <span>Universe</span>
          </div>

          <ArrowForward />

          <div className="enterpriseScaleNode">
            <div>
              <Analytics />
            </div>
            <span>Intelligence</span>
          </div>

        </div>

      </section>

      {/* ================================================================
          ENTERPRISE CAPABILITIES
      ================================================================ */}

      <section className="enterpriseCapabilitiesSection">

        <div className="enterpriseSectionHeading">
          <span className="enterpriseSectionKicker">
            ENTERPRISE CAPABILITIES
          </span>

          <h2>
            The infrastructure behind
            <span> operational control.</span>
          </h2>

          <p>
            Designed to give growing organizations the structure, visibility,
            and flexibility required to operate inventory at scale.
          </p>
        </div>

        <div className="enterpriseCapabilityGrid">

          {ENTERPRISE_CAPABILITIES.map((item, index) => (
            <article
              className="enterpriseCapabilityCard"
              key={index}
            >
              <div className="enterpriseCapabilityNumber">
                {String(index + 1).padStart(2, "0")}
              </div>

              <div className="enterpriseCapabilityIcon">
                {item.icon}
              </div>

              <h3>{item.title}</h3>

              <p>{item.description}</p>

              <ArrowForward className="enterpriseCapabilityArrow" />
            </article>
          ))}

        </div>

      </section>

      {/* ================================================================
          CONTROL SECTION
      ================================================================ */}

      <section className="enterpriseControlSection">

        <div className="enterpriseControlVisual">

          <div className="enterpriseControlCircle circleOne" />
          <div className="enterpriseControlCircle circleTwo" />
          <div className="enterpriseControlCircle circleThree" />

          <div className="enterpriseControlCore">
            <Hub />
            <span>UNIVERSE</span>
            <small>CONTROL CENTER</small>
          </div>

          <div className="enterpriseControlNode nodeTop">
            <Warehouse />
            <span>Warehouses</span>
          </div>

          <div className="enterpriseControlNode nodeRight">
            <Storefront />
            <span>Branches</span>
          </div>

          <div className="enterpriseControlNode nodeBottom">
            <PointOfSale />
            <span>Sales</span>
          </div>

          <div className="enterpriseControlNode nodeLeft">
            <Groups />
            <span>Teams</span>
          </div>

        </div>

        <div className="enterpriseControlContent">

          <span className="enterpriseSectionKicker">
            CENTRALIZED CONTROL
          </span>

          <h2>
            See the whole business.
            <span> Control what matters.</span>
          </h2>

          <p>
            Enterprise operations become difficult when information is
            scattered across branches, spreadsheets, separate systems, and
            disconnected teams. Universe brings those operational layers
            together so management can work from a shared picture.
          </p>

          <div className="enterpriseControlList">

            <div>
              <CheckCircle />
              <span>
                Centralized visibility across locations
              </span>
            </div>

            <div>
              <CheckCircle />
              <span>
                Location-aware inventory operations
              </span>
            </div>

            <div>
              <CheckCircle />
              <span>
                Structured roles and responsibilities
              </span>
            </div>

            <div>
              <CheckCircle />
              <span>
                Operational data available for better decisions
              </span>
            </div>

          </div>

        </div>

      </section>

      {/* ================================================================
          SOLUTIONS
      ================================================================ */}

      <section className="enterpriseSolutionsSection">

        <div className="enterpriseSectionHeading centered">

          <span className="enterpriseSectionKicker">
            DESIGNED FOR COMPLEX OPERATIONS
          </span>

          <h2>
            One platform.
            <span> Many enterprise environments.</span>
          </h2>

          <p>
            Whether your organization operates stores, warehouses,
            distribution networks, or corporate inventory, Universe can become
            the operational layer connecting them.
          </p>

        </div>

        <div className="enterpriseSolutionGrid">

          {ENTERPRISE_SOLUTIONS.map((solution, index) => (
            <article
              className="enterpriseSolutionCard"
              key={index}
            >

              <div className="enterpriseSolutionIcon">
                {solution.icon}
              </div>

              <span className="enterpriseSolutionNumber">
                0{index + 1}
              </span>

              <h3>{solution.title}</h3>

              <p>{solution.description}</p>

              <div className="enterpriseSolutionPoints">

                {solution.points.map((point, pointIndex) => (
                  <div key={pointIndex}>
                    <CheckCircle />
                    <span>{point}</span>
                  </div>
                ))}

              </div>

            </article>
          ))}

        </div>

      </section>

      {/* ================================================================
          ECOSYSTEM
      ================================================================ */}

      <section className="enterpriseEcosystemSection">

        <div className="enterpriseEcosystemHeader">

          <div>
            <span className="enterpriseSectionKicker">
              THE UNIVERSE ECOSYSTEM
            </span>

            <h2>
              More than inventory.
              <span> A connected business ecosystem.</span>
            </h2>
          </div>

          <p>
            Enterprise organizations need more than a database of products.
            Universe connects the tools people use across the operational
            journey.
          </p>

        </div>

        <div className="enterpriseAppGrid">

          {ENTERPRISE_APPS.map((app) => (
            <article className="enterpriseAppCard" key={app.number}>

              <span className="enterpriseAppNumber">
                {app.number}
              </span>

              <div className="enterpriseAppIcon">
                {app.icon}
              </div>

              <h3>{app.title}</h3>

              <p>{app.description}</p>

              <div className="enterpriseAppLink">
                Explore application
                <ArrowForward />
              </div>

            </article>
          ))}

        </div>

      </section>

      {/* ================================================================
          SECURITY
      ================================================================ */}

      <section className="enterpriseSecuritySection">

        <div className="enterpriseSecurityContent">

          <span className="enterpriseSectionKicker">
            ENTERPRISE GOVERNANCE
          </span>

          <h2>
            Give your people access.
            <span> Keep your business under control.</span>
          </h2>

          <p>
            Large teams need structure. Universe helps organizations create
            clear boundaries around responsibilities, operational access, and
            business information.
          </p>

          <button className="enterpriseOutlineButton">
            Explore security & permissions
            <ArrowForward />
          </button>

        </div>

        <div className="enterpriseSecurityGrid">

          {SECURITY_ITEMS.map((item, index) => (
            <div
              className="enterpriseSecurityCard"
              key={index}
            >

              <div className="enterpriseSecurityIcon">
                {item.icon}
              </div>

              <h3>{item.title}</h3>

              <p>{item.description}</p>

            </div>
          ))}

        </div>

      </section>

      {/* ================================================================
          PERFORMANCE
      ================================================================ */}

      <section className="enterprisePerformanceSection">

        <div className="enterprisePerformanceHeader">

          <span className="enterpriseSectionKicker">
            BUILT FOR DECISION MAKERS
          </span>

          <h2>
            From stock movement
            <span> to business intelligence.</span>
          </h2>

          <p>
            The goal is not simply to record what happened. It is to help
            leadership understand what is happening across the organization.
          </p>

        </div>

        <div className="enterprisePerformanceCards">

          <div className="enterprisePerformanceCard">

            <div className="enterprisePerformanceIcon">
              <Speed />
            </div>

            <strong>Faster Operations</strong>

            <p>
              Reduce unnecessary manual work by connecting inventory workflows
              and operational processes.
            </p>

          </div>

          <div className="enterprisePerformanceCard featured">

            <div className="enterprisePerformanceIcon">
              <Assessment />
            </div>

            <strong>Better Decisions</strong>

            <p>
              Transform operational activity into meaningful reports,
              analytics, and management insight.
            </p>

          </div>

          <div className="enterprisePerformanceCard">

            <div className="enterprisePerformanceIcon">
              <SwapHoriz />
            </div>

            <strong>Greater Traceability</strong>

            <p>
              Understand inventory movement across the business and create a
              clearer operational history.
            </p>

          </div>

        </div>

      </section>

      {/* ================================================================
          FAQ
      ================================================================ */}

      <section className="enterpriseFaqSection">

        <div className="enterpriseSectionHeading centered">

          <span className="enterpriseSectionKicker">
            ENTERPRISE QUESTIONS
          </span>

          <h2>
            Built for organizations
            <span> ready to operate at another level.</span>
          </h2>

        </div>

        <div className="enterpriseFaqList">

          {FAQS.map((faq, index) => {

            const isOpen = activeFaq === index;

            return (
              <div
                className={
                  isOpen
                    ? "enterpriseFaqItem enterpriseFaqItemOpen"
                    : "enterpriseFaqItem"
                }
                key={index}
              >

                <button
                  type="button"
                  onClick={() =>
                    setActiveFaq(isOpen ? null : index)
                  }
                >
                  <span>{faq.question}</span>

                  <ExpandMore
                    className={
                      isOpen
                        ? "enterpriseFaqArrow enterpriseFaqArrowOpen"
                        : "enterpriseFaqArrow"
                    }
                  />
                </button>

                {isOpen && (
                  <div className="enterpriseFaqAnswer">
                    <p>{faq.answer}</p>
                  </div>
                )}

              </div>
            );
          })}

        </div>

      </section>

      {/* ================================================================
          FINAL CTA
      ================================================================ */}

      <section className="enterpriseFinalCta">

        <div className="enterpriseCtaGlow" />

        <div className="enterpriseCtaContent">

          <div className="enterpriseCtaIcon">
            <RocketLaunch />
          </div>

          <span className="enterpriseSectionKicker">
            READY FOR THE NEXT STAGE?
          </span>

          <h2>
            Your business is growing.
            <span> Your inventory system should be ready.</span>
          </h2>

          <p>
            Talk with the Universe team about your organization's operations,
            locations, users, workflows, and enterprise requirements.
          </p>

          <div className="enterpriseCtaActions">

            <button className="enterprisePrimaryButton">
              Talk to Enterprise
              <ArrowForward />
            </button>

            <button className="enterpriseCtaTextButton">
              Contact Universe
              <ArrowForward />
            </button>

          </div>

        </div>

      </section>

    </main>
  );
};

export default Enterprise;